/**
 * The booking calendar's rules (ui#496). Plain TypeScript — no React — because "which day can I click,
 * and what does clicking it do" is exactly the rule that dies inside a component and gets re-derived by
 * hand on the next restyle.
 *
 * Dates are ISO `YYYY-MM-DD` strings throughout and every comparison is lexical or UTC-based. No local
 * `Date` construction: `new Date("2026-09-01")` is UTC midnight but `new Date(2026, 8, 1)` is LOCAL
 * midnight, and a calendar that mixes the two silently shifts a whole booking by a day for anyone west
 * of Greenwich.
 */

/** What a cell in the month grid is. */
export const DayState = {
	/** Leading/trailing blank so the grid is whole weeks. */
	Padding: "padding",
	/** Bookable. */
	Available: "available",
	/** Part of the sponsor's current selection. */
	Selected: "selected",
	/** Held by an APPROVED booking. Unbuyable. */
	Booked: "booked",
	/** Paid but awaiting review — still winnable, because review can reject it. */
	Pending: "pending",
	/** Outside the bookable window the api published. */
	OutOfWindow: "outOfWindow",
} as const;

export type DayStateValue = (typeof DayState)[keyof typeof DayState];

export interface SponsorAvailability {
	from: string;
	to: string;
	maxDays: number;
	bookedDates: readonly string[];
	pendingDates: readonly string[];
}

export interface DateRange {
	startsOn: string;
	endsOn: string;
}

export interface DayCell {
	/** `null` only for padding cells. */
	date: string | null;
	state: DayStateValue;
}

const DAY_MS = 86_400_000;

function utc(date: string): number {
	return Date.parse(`${date}T00:00:00Z`);
}

function iso(timestamp: number): string {
	return new Date(timestamp).toISOString().slice(0, 10);
}

/** An ISO day shifted by whole days. UTC throughout, like every other comparison in this module. */
export function shiftDate(date: string, delta: number): string {
	return iso(utc(date) + delta * DAY_MS);
}

/**
 * Monday-first weekday index, 0 for Monday — the column order {@link buildMonthGrid} lays days out in
 * and the order `sponsor.calendar.weekdays` is written in. `getUTCDay()` is Sunday-first, so Sunday
 * becomes the 7th column rather than the 1st.
 */
export function weekdayIndex(date: string): number {
	return (new Date(utc(date)).getUTCDay() + 6) % 7;
}

/** Every ISO day in an inclusive range. */
export function rangeDates(range: DateRange): string[] {
	const days: string[] = [];
	for (let at = utc(range.startsOn); at <= utc(range.endsOn); at += DAY_MS) {
		days.push(iso(at));
	}
	return days;
}

/** Inclusive day count; both ends are days the takeover runs. */
export function rangeDays(range: DateRange | null): number {
	if (range === null) {
		return 0;
	}
	return Math.round((utc(range.endsOn) - utc(range.startsOn)) / DAY_MS) + 1;
}

/** `YYYY-MM` shifted by whole months, rolling the year correctly in both directions. */
export function shiftMonth(month: string, delta: number): string {
	const [year, index] = month.split("-").map(Number);
	const shifted = new Date(Date.UTC(year, index - 1 + delta, 1));
	return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}`;
}

/**
 * The month and year as NUMBERS, so the view looks the month name up in the catalog rather than
 * formatting a date itself — `toLocaleString` would emit English on a Romanian page.
 */
export function monthLabelParts(month: string): {month: number; year: number} {
	const [year, index] = month.split("-").map(Number);
	return {month: index, year};
}

/** Whether a day is bookable at all: inside the window and not already held. */
export function isSelectable(date: string, availability: SponsorAvailability): boolean {
	if (date < availability.from || date > availability.to) {
		return false;
	}
	return !availability.bookedDates.includes(date);
}

/**
 * The grid for one month, Monday-first and padded to whole weeks.
 *
 * Booked wins over selected deliberately. The selection machine already refuses to span a held day, but
 * a grid that painted "selected" over "booked" would tell a sponsor they had bought something they had
 * not — and that is a claim about money.
 */
export function buildMonthGrid(
	month: string,
	availability: SponsorAvailability,
	selection: DateRange | null,
): DayCell[] {
	const {month: index, year} = monthLabelParts(month);
	const firstOfMonth = Date.UTC(year, index - 1, 1);
	const daysInMonth = new Date(Date.UTC(year, index, 0)).getUTCDate();
	const leadingBlanks = weekdayIndex(iso(firstOfMonth));

	const cells: DayCell[] = [];
	for (let blank = 0; blank < leadingBlanks; blank++) {
		cells.push({date: null, state: DayState.Padding});
	}

	const selected = selection === null ? [] : rangeDates(selection);
	for (let day = 0; day < daysInMonth; day++) {
		const date = iso(firstOfMonth + day * DAY_MS);
		cells.push({date, state: dayStateOf(date, availability, selected)});
	}

	while (cells.length % 7 !== 0) {
		cells.push({date: null, state: DayState.Padding});
	}
	return cells;
}

/**
 * What one day IS, given the published availability and whatever is currently selected.
 *
 * Exported because the grid is no longer the only surface that asks: the `/advertising` landing page's
 * fourteen-day row asks the same question (ui#1861), and a second answer written beside it would be the
 * one that quietly disagrees about pending days — which are HELD, not free, on both surfaces.
 *
 * `selected` defaults to empty for callers that show availability without a selection to paint.
 */
export function dayStateOf(
	date: string,
	availability: SponsorAvailability,
	selected: readonly string[] = [],
): DayStateValue {
	if (date < availability.from || date > availability.to) {
		return DayState.OutOfWindow;
	}
	if (availability.bookedDates.includes(date)) {
		return DayState.Booked;
	}
	if (selected.includes(date)) {
		return DayState.Selected;
	}
	if (availability.pendingDates.includes(date)) {
		return DayState.Pending;
	}
	return DayState.Available;
}

/**
 * The click-to-select state machine.
 *
 * One click starts a range; a second, later click extends it. Anything else RESTARTS at the clicked day
 * rather than silently producing something the sponsor did not ask for — an earlier second click, a
 * third click, a span longer than the maximum, or a span across a held day. Restarting is the honest
 * response: truncating an over-long drag to seven days would quietly sell them a different booking from
 * the one they dragged.
 *
 * @returns the next selection, or `null` when the click lands on a day that cannot be booked at all
 */
export function nextRangeSelection(
	current: DateRange | null,
	clicked: string,
	availability: SponsorAvailability,
): DateRange | null {
	if (!isSelectable(clicked, availability)) {
		return current;
	}
	const fresh = {startsOn: clicked, endsOn: clicked};
	if (current === null) {
		return fresh;
	}
	// A complete range means the previous attempt is finished; this click begins the next one.
	const isPartial = current.startsOn === current.endsOn;
	if (!isPartial || clicked <= current.startsOn) {
		return fresh;
	}
	const candidate = {startsOn: current.startsOn, endsOn: clicked};
	if (rangeDays(candidate) > availability.maxDays || spansHeldDay(candidate, availability)) {
		return fresh;
	}
	return candidate;
}

/** Whether a candidate range would cover a day an approved booking already holds. */
function spansHeldDay(range: DateRange, availability: SponsorAvailability): boolean {
	return rangeDates(range).some(date => availability.bookedDates.includes(date));
}

/**
 * The part of a selection that survives a freshly-read calendar — the whole range, or nothing.
 *
 * Needed because the calendar a sponsor picked from can go stale under them: an operator approves a
 * booking, and dates that were free when the page loaded are now held (api#502). Keeping a range the
 * server would now refuse leaves "3 days, 747 RON" on screen beneath a banner saying the period was
 * taken, and the next Continue earns the identical refusal — so a range that has lost a day is dropped
 * whole rather than trimmed. Trimming would quietly sell a DIFFERENT booking from the one they chose,
 * which is the same reason {@link nextRangeSelection} restarts instead of truncating an over-long drag.
 */
export function selectionSurviving(
	selection: DateRange | null,
	availability: SponsorAvailability,
): DateRange | null {
	if (selection === null) {
		return null;
	}
	const insideWindow = selection.startsOn >= availability.from && selection.endsOn <= availability.to;
	// The same three conditions nextRangeSelection refuses a click for, in the same order. Stated as one
	// rule rather than two: a selection that survives here must be one the click machine would have let
	// the sponsor build, or the two disagree about what is bookable — and maxDays is operator policy that
	// can be lowered between the two reads.
	const withinMaximum = rangeDays(selection) <= availability.maxDays;
	return insideWindow && withinMaximum && !spansHeldDay(selection, availability) ? selection : null;
}

/**
 * How a spec addresses the calendar's own box.
 *
 * Here rather than beside the component, for the reason ui#567 gave the takeover its own testid module:
 * `e2e/` is type-checked by `tsconfig.node.json`, which carries no styled-components theme declaration,
 * so importing this from the component would drag `SponsorCalendar.styles.ts` into that project and fail
 * it on every `theme.tokens` read. The grid has no accessible name of its own either — its days and its
 * month heading carry the semantics — so there is no role-and-name selector that resolves to the frame,
 * which is the box whose size ui#572 is about.
 */
export const SPONSOR_CALENDAR_TESTID = "sponsor-calendar";
